import React, { useState } from 'react';
import { SearchBox } from '@mapbox/search-js-react';
import { PinGlyph } from './host/kit';

const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN;

/**
 * AddressAutocomplete — Mapbox Search Box (search-js-react), drawn as the LOCATION row of
 * the host event edit handoff: a 48px grey pill with a pin at left 18px, black border +
 * white fill on focus, and a white radius-20 results panel with no shadow.
 *
 * Upgraded from MapboxGeocoder so users can find venues by business name
 * (e.g. "Van Westen Vineyards") and lat/long-only places (parks, beaches)
 * instead of needing an exact street address. Search Box has proper POI
 * coverage; the classic geocoder did not.
 *
 * Contract preserved from the old component:
 *   onSelect({ address, latitude, longitude, city })  — fired when a suggestion is picked
 *   onChange(e)                                       — fired on every keystroke
 *   onBlur                                            — fired on input blur
 *   value, error, disabled, label, required           — same as before
 *
 * The wizard wraps this in a kit `Field` (label + error line) and passes `label={null}`;
 * a non-empty `label` still prints a `.hev-label` for older call sites. The error
 * message is never printed here — `error` only paints the red border.
 *
 * The SearchBox's inner input is not our element, so everything visual goes through its
 * `theme` (variables + cssText on `.SearchBox` / `.Input` / `.Results` / `.Suggestion`).
 */
export default function AddressAutocomplete({
  value = '',
  onSelect,
  onChange,
  onBlur,
  error,
  disabled = false,
  label = 'Location',
  required = false,
}) {
  // SearchBox is controlled — track the input text locally and forward
  // every change up via onChange so the parent's freeform state stays in sync.
  const [text, setText] = useState(value || '');

  // Keep local in sync if the parent rehydrates a value (e.g. edit mode).
  React.useEffect(() => {
    if ((value || '') !== text) setText(value || '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const handleChange = (newValue) => {
    setText(newValue);
    onChange?.({ target: { name: 'location', value: newValue } });
  };

  const handleRetrieve = (response) => {
    const feature = response?.features?.[0];
    if (!feature) return;
    const props = feature.properties || {};
    const coords = feature.geometry?.coordinates || [];
    const ctx = props.context || {};

    // Build a "City, REGION" string that matches the old extractCity output.
    const cityName = ctx.place?.name || ctx.locality?.name || ctx.neighborhood?.name || '';
    const regionCode = ctx.region?.region_code || ctx.region?.name || '';
    const city = cityName && regionCode ? `${cityName}, ${regionCode}` : (cityName || regionCode || '');

    // Prefer the full address (POIs include both name + street); fall back to name.
    const address = props.full_address || props.place_formatted || props.name || '';
    setText(address);
    onSelect?.({
      address,
      latitude: coords[1],
      longitude: coords[0],
      city,
    });
  };

  const borderColor = error ? '#DC2626' : 'transparent';

  return (
    <div style={{ width: '100%', minWidth: 0 }}>
      {label && (
        <span className="hev-label" style={{ display: 'block', marginBottom: 8 }}>
          {label}
          {required && <span className="hev-req"> *</span>}
        </span>
      )}
      <span
        className="hev-affix"
        data-icon=""
        onBlur={onBlur}
        style={disabled ? { opacity: 0.5, pointerEvents: 'none' } : undefined}
      >
        <SearchBox
          accessToken={MAPBOX_TOKEN}
          value={text}
          onChange={handleChange}
          onRetrieve={handleRetrieve}
          placeholder="Start typing an address…"
          options={{
            language: 'en',
            country: 'us,ca',
            limit: 10,
            types: 'address,poi,place,locality,neighborhood,street',
          }}
          theme={{
            variables: {
              fontFamily: '"JetBrains Mono", Menlo, monospace',
              fontFamilyMobile: '"JetBrains Mono", Menlo, monospace',
              unit: '14px',
              padding: '0',
              boxShadow: 'none',
              border: `2px solid ${borderColor}`,
              borderRadius: '999px',
              colorBackground: '#F5F5F5',
              colorBackgroundHover: '#000',
              colorBackgroundActive: '#000',
              colorText: '#000',
              colorPrimary: '#000',
              colorSecondary: '#6B7280',
            },
            cssText: `
              .SearchBox { transition: border-color .2s, background-color .2s; }
              .SearchBox:focus-within { border-color: ${error ? '#DC2626' : '#000'}; background-color: #fff; }
              .SearchIcon { display: none; }
              .Input { height: 44px; padding: 0 40px 0 42px; color: #000; font-size: 14px; }
              .Input:focus { border: 0; box-shadow: none; color: #000; }
              .Input::placeholder { color: #9CA3AF; }
              .ActionIcon { right: 12px; color: #6B7280; }
              .Results { margin-top: 6px; border: none; border-radius: 20px; background-color: #fff; box-shadow: none; padding: 6px; }
              .Suggestion { padding: 10px 14px; border-radius: 14px; color: #000; transition: background-color .2s, color .2s; }
              .Suggestion:hover, .Suggestion[aria-selected="true"], .Suggestion:active { background-color: #000; color: #fff; }
              .SuggestionName { font-size: 13px; font-weight: 600; color: inherit; }
              .SuggestionDesc { font-size: 10px; letter-spacing: .1em; color: inherit; }
              .Suggestion:hover .SuggestionDesc, .Suggestion[aria-selected="true"] .SuggestionDesc { color: #9CA3AF; }
              .ResultsAttribution { padding: 6px 14px 4px; font-size: 9px; }
              .ResultsAttribution a { color: #9CA3AF; }
            `,
          }}
        />
        {/* After the SearchBox so it paints above the pill; hostEvent.css places it at left 18px. */}
        <PinGlyph />
      </span>
    </div>
  );
}
